import { ALL_LOCATIONS_KEY, PermissionsByLocation } from './LocationPermissions';
import { IPermission } from './models';

export const PRODUCTS: { code: string; label: string }[] = [
    { code: 'access_subs', label: 'Subs' },
    { code: 'access_recruit', label: 'Recruit' },
];

export const PERMISSION_PARENT: Record<string, string> = {
    post_job: 'access_subs',
};

export interface IPermissionGroup {
    key: string;
    label: string;
    access?: IPermission;
    children: IPermission[];
}

export function groupPermissionsByProduct(permissions: IPermission[]): IPermissionGroup[] {
    const productGroups: IPermissionGroup[] = PRODUCTS.map((product) => ({
        key: product.code,
        label: product.label,
        access: permissions.find((x) => x.code === product.code),
        children: permissions.filter((x) => PERMISSION_PARENT[x.code] === product.code),
    })).filter((group) => group.access || group.children.length);
    const productCodes = new Set(PRODUCTS.map((x) => x.code));
    const accountPermissions = permissions.filter((x) => !productCodes.has(x.code) && !PERMISSION_PARENT[x.code]);
    const accountGroup: IPermissionGroup[] = accountPermissions.length
        ? [{ key: 'account', label: 'Account', children: accountPermissions }]
        : [];
    return [...productGroups, ...accountGroup];
}

function idByCode(permissions: IPermission[], code: string): number | undefined {
    return permissions.find((x) => x.code === code)?.id;
}

export function parentPermissionId(permissions: IPermission[], permissionId: number): number | undefined {
    const code = permissions.find((x) => x.id === permissionId)?.code;
    const parentCode = code ? PERMISSION_PARENT[code] : undefined;
    return parentCode ? idByCode(permissions, parentCode) : undefined;
}

export function childPermissionIds(permissions: IPermission[], permissionId: number): number[] {
    const code = permissions.find((x) => x.id === permissionId)?.code;
    if (!code) return [];
    return Object.entries(PERMISSION_PARENT)
        .filter(([, parentCode]) => parentCode === code)
        .map(([childCode]) => idByCode(permissions, childCode))
        .filter((id): id is number => id !== undefined);
}

function applyPermission(existing: number[], permissionId: number, checked: boolean, permissions: IPermission[]) {
    const next = new Set(existing);
    if (checked) {
        next.add(permissionId);
        const parent = parentPermissionId(permissions, permissionId);
        if (parent !== undefined) next.add(parent);
    } else {
        next.delete(permissionId);
        childPermissionIds(permissions, permissionId).forEach((child) => next.delete(child));
    }
    return Array.from(next);
}

export interface IContactDetailRow {
    business_location: number | null;
    permissions: number[];
}

export function togglePermission(
    current: PermissionsByLocation,
    locationKey: number,
    permissionId: number,
    permissions: IPermission[],
): PermissionsByLocation {
    const existing = current[locationKey] ?? [];
    const checked = !existing.includes(permissionId);
    return { ...current, [locationKey]: applyPermission(existing, permissionId, checked, permissions) };
}

export function buildPermissionPayload(
    selectedLocations: number[],
    allLocationsSelected: boolean,
    permissionsByLocation: PermissionsByLocation,
) {
    if (allLocationsSelected) {
        return {
            business_locations: [] as number[],
            permissions: permissionsByLocation[ALL_LOCATIONS_KEY] ?? [],
        };
    }
    const locationPermissions = selectedLocations.map((id) => ({
        business_location: id,
        permissions: permissionsByLocation[id] ?? [],
    }));

    const union = Array.from(new Set(locationPermissions.flatMap((x) => x.permissions)));
    return {
        business_locations: selectedLocations,
        permissions: union,
        location_permissions: locationPermissions,
    };
}

export function fromContactDetail(rows: IContactDetailRow[], allLocationIds: number[]) {
    const businessWide = rows.find((row) => row.business_location === null);
    if (businessWide || rows.length === 0) {
        return {
            allLocationsSelected: true,
            selectedLocations: allLocationIds,
            permissionsByLocation: { [ALL_LOCATIONS_KEY]: businessWide?.permissions ?? [] } as PermissionsByLocation,
        };
    }
    const permissionsByLocation: PermissionsByLocation = {};
    rows.forEach((row) => {
        permissionsByLocation[row.business_location as number] = row.permissions;
    });
    return {
        allLocationsSelected: false,
        selectedLocations: rows.map((row) => row.business_location as number),
        permissionsByLocation,
    };
}

export function copyPermissionsToLocations(
    current: PermissionsByLocation,
    fromKey: number,
    toKeys: number[],
): PermissionsByLocation {
    const source = current[fromKey] ?? [];
    const next: PermissionsByLocation = { ...current };
    toKeys.forEach((key) => {
        next[key] = Array.from(source);
    });
    return next;
}

export interface IAllLocationsSnapshot {
    individual: PermissionsByLocation;
    selected: number[];
    seed: number[];
}

export function enterAllLocationsMode(
    current: PermissionsByLocation,
    locationIds: number[],
): { next: PermissionsByLocation; snapshot: IAllLocationsSnapshot } {
    const sets = locationIds.map((id) => current[id] ?? []);
    const common = sets.length
        ? sets.reduce((acc, permissions) => acc.filter((id) => permissions.includes(id)))
        : current[ALL_LOCATIONS_KEY] ?? [];
    const seed = current[ALL_LOCATIONS_KEY]?.length ? current[ALL_LOCATIONS_KEY] : common;
    return {
        next: { ...current, [ALL_LOCATIONS_KEY]: Array.from(seed) },
        snapshot: { individual: current, selected: Array.from(locationIds), seed: Array.from(seed) },
    };
}

export function leaveAllLocationsMode(
    current: PermissionsByLocation,
    locationIds: number[],
    snapshot: IAllLocationsSnapshot | null,
): PermissionsByLocation {
    const shared = current[ALL_LOCATIONS_KEY] ?? [];
    const unchanged =
        snapshot !== null && shared.length === snapshot.seed.length && shared.every((id) => snapshot.seed.includes(id));
    if (unchanged) {
        return { ...snapshot.individual };
    }
    const next: PermissionsByLocation = { ...current };
    locationIds.forEach((id) => {
        next[id] = Array.from(shared);
    });
    return next;
}
